import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '~/components/ui/card';
import { Skeleton } from '~/components/ui/skeleton';

import type { CategoryKpis } from '../../types/experiment.types';
import { formatPercentFromPercent } from '../../utils/format-percent';

import { ExperimentKpiCard } from './experiment-kpi-card';

interface Props {
  kpis: CategoryKpis | undefined;
  isLoading: boolean;
}

/** Five lift cells shown at the top of the category-mode Performance tab (matches screenshot 17). */
export function ExperimentCategoryKpiCards({ kpis, isLoading }: Props) {
  const { t } = useTranslation('experiments');

  // Only skeleton while the query is in flight. Once it resolves with no
  // aggregate (`kpis` undefined — the endpoint exposes no overall lift yet),
  // fall through and render the five cells with a "—" placeholder rather than a
  // perpetual skeleton.
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-5">
        {Array.from({ length: 5 }, (_, i) => (
          <Card key={i}>
            <CardContent className="space-y-2 p-5">
              <Skeleton className="h-8 w-24" />
              <Skeleton className="h-4 w-28" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // `?? NaN`: the percent formatter renders NaN as "—", so a missing
  // aggregate shows an explicit empty state, not "0%".
  const cells: { label: string; value: number }[] = [
    {
      label: t('detail.categoryKpis.revenueLift', 'Revenue Lift'),
      value: kpis?.revenueLift ?? Number.NaN,
    },
    {
      label: t('detail.categoryKpis.conversionValueLift', 'Conv. Value Lift'),
      value: kpis?.conversionValueLift ?? Number.NaN,
    },
    {
      label: t('detail.categoryKpis.conversionLift', 'Conversion Lift'),
      value: kpis?.conversionLift ?? Number.NaN,
    },
    {
      label: t('detail.categoryKpis.impressionLift', 'Impression Lift'),
      value: kpis?.impressionLift ?? Number.NaN,
    },
    {
      label: t('detail.categoryKpis.clickLift', 'Click Lift'),
      value: kpis?.clickLift ?? Number.NaN,
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-5">
      {cells.map((cell) => (
        <ExperimentKpiCard
          key={cell.label}
          label={cell.label}
          value={formatPercentFromPercent(cell.value, { precision: 'chart-1dp' })}
          tone="default"
        />
      ))}
    </div>
  );
}
